import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Menu, Globe, ChevronDown } from 'lucide-react';
import { LicenseBadge } from '@/components/fintech/LicenseBadge';

export function PublicHeader() {
  const { t, lang, langs, setLang } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const nav = [
    { to: '/', label: t('nav.home') },
    { to: '/about', label: t('nav.about') },
    { to: '/services', label: t('nav.services') },
    { to: '/business', label: t('nav.business') },
    { to: '/documents', label: t('nav.documents') },
    { to: '/contact', label: t('nav.contact') },
  ];

  return (
    <header className="sticky top-0 z-50 border-b bg-[hsl(var(--background))]/95 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--background))]/80">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2" data-testid="header-logo-link">
          <img src="/logo.png" alt="Concorde Financial Limited" className="h-10 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {nav.map(item => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace('/', '') || 'home'}`}
              className={`text-sm font-medium transition-colors ${location.pathname === item.to ? 'text-[hsl(var(--foreground))]' : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'}`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" data-testid="header-language-switcher" className="gap-1">
                <Globe className="h-4 w-4" />
                {langs.find(l => l.code === lang)?.label}
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {langs.map(l => (
                <DropdownMenuItem key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-${l.code}`}>
                  {l.label} — {l.full}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <Button asChild size="sm" data-testid="header-dashboard-button" className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]">
                <Link to="/wallet">{t('header.dashboard')}</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" data-testid="header-login-button">
                  <Link to="/login">{t('header.login')}</Link>
                </Button>
                <Button asChild size="sm" data-testid="header-register-button" className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]">
                  <Link to="/register">{t('header.register')}</Link>
                </Button>
              </>
            )}
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="lg:hidden" data-testid="header-mobile-menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-white w-80">
              <div className="flex flex-col gap-3 mt-8">
                {nav.map(item => (
                  <Link key={item.to} to={item.to} onClick={() => setOpen(false)} className="text-sm font-medium py-2">
                    {item.label}
                  </Link>
                ))}
                <div className="h-px bg-border my-2" />
                {user ? (
                  <Button asChild className="bg-[hsl(var(--brand-navy))]"><Link to="/wallet" onClick={() => setOpen(false)}>{t('header.dashboard')}</Link></Button>
                ) : (
                  <>
                    <Button asChild variant="outline"><Link to="/login" onClick={() => setOpen(false)}>{t('header.login')}</Link></Button>
                    <Button asChild className="bg-[hsl(var(--brand-navy))]"><Link to="/register" onClick={() => setOpen(false)}>{t('header.register')}</Link></Button>
                  </>
                )}
                <div className="pt-4"><LicenseBadge /></div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
